/**
 * NELOW — PDP gallery ordering by ALT tag (#NN).
 *
 * This script is PURELY ADDITIVE on top of Eurus's native variant-media
 * filtering (assets/variant-select.js, snippets/media-gallery.liquid,
 * snippets/media.liquid). It does not touch any of that logic, any markup,
 * class, or style — only the relative order of existing DOM nodes within
 * their existing container.
 *
 * What it does:
 * - Reads the raw (unstripped) ALT text of every product media item from a
 *   small JSON data island injected by layout/theme.liquid
 *   (<script type="application/json" data-nelow-media-order="...">).
 * - Extracts a position number AND a color token from each "#NN #Color"
 *   ALT text (e.g. "#01 #Noir" -> color "Noir", position 1). Native Eurus
 *   filtering already reads the LAST "#" token as the color/"#ALL" marker,
 *   so the position tag must come BEFORE the color tag to stay compatible:
 *     "#01 #Noir"   -> color = Noir, position = 1
 *     "#04 #ALL"    -> color = ALL (shown for every color), position = 4
 *   Media with no "#NN" tag keep their original relative order and are
 *   sorted after any explicitly numbered media.
 *
 * - Position numbers are NOT globally unique: they restart at #01 for every
 *   color (e.g. Noir #01 and Ecru #01 are two different media items that
 *   both carry position 1). All of a product's media (every color) sit as
 *   siblings in the same DOM container at all times — Eurus never removes
 *   inactive-color media from the DOM, it only toggles classes, and does so
 *   only in reaction to a variant "change" event (never on a fresh page
 *   load). Sorting ALL siblings together by that non-unique number would
 *   therefore interleave a different color's media into the currently
 *   displayed color's sequence. To avoid that, every reorder pass:
 *     1. Determines which color is currently active for that container.
 *     2. Sorts ONLY the media belonging to that color + "#ALL" media by
 *        their #NN position.
 *     3. Moves that sorted subset to the front of the container, leaving
 *        every other color's media untouched and in their existing
 *        relative order after it (mirrors how Eurus's own
 *        _setActiveMedia() moves the active set to the front via
 *        prepend()).
 *   This makes the result deterministic and idempotent: re-running it any
 *   number of times, at any point, for the same active color, always
 *   converges to the same order, independent of Splide's own load timing.
 *
 * - The active color is detected, in order of preference:
 *     a) From any media already flagged `.media_active` by Eurus (set by
 *        assets/variant-select.js's _setActiveMedia(), i.e. after a swatch
 *        change) — this is the ground truth once Eurus has run.
 *     b) Otherwise (fresh page load, before any variant change), from the
 *        FIRST media in the container's current DOM order — since
 *        snippets/media-gallery.liquid always server-renders the selected
 *        variant's featured media first, this reliably identifies the
 *        initially selected color even before any JS runs.
 *   If neither yields a resolvable color (or only "#ALL" media are found),
 *   the container is left untouched rather than risk sorting the wrong set.
 *
 * - Re-runs on: DOMContentLoaded, a MutationObserver on each container,
 *   Eurus's own `eurus:product-page-variant-select:updated:<sectionId>`
 *   event (fired after a swatch change), `shopify:section:load` (quick
 *   view / AJAX), and `slider loaded` — the event assets/theme.js's
 *   deferScriptLoad() dispatches once Splide itself has actually finished
 *   loading and mounting. On a real storefront visit (Shopify.designMode
 *   false), Splide is only loaded on the first user interaction
 *   (mouseover/wheel/scroll/keydown) or after a 1-3s timeout — a race
 *   against this script's own DOMContentLoaded pass. Listening for
 *   `slider loaded` guarantees a final, correct resync happens exactly
 *   when Splide actually mounts, whenever that turns out to be, instead of
 *   depending on winning that race.
 */
(function () {
  window.Eurus = window.Eurus || {};
  window.Eurus.loadedScript = window.Eurus.loadedScript || new Set();
  if (window.Eurus.loadedScript.has('nelow-pdp-gallery-order.js')) return;
  window.Eurus.loadedScript.add('nelow-pdp-gallery-order.js');

  var POSITION_RE = /#0*(\d{1,4})(?:\s|$)/;
  var MEDIA_ID_RE = /-(\d+)$/;
  var ALL_COLOR = 'ALL';

  // mediaId (string) -> position (number) / color (string). Shared across all
  // sections/instances on the page (media ids are globally unique), built
  // from every data island found, so quick-view / duplicated layout
  // branches are covered too.
  var positionByMediaId = {};
  var colorByMediaId = {};
  var sectionIds = [];

  function parsePosition(alt) {
    if (!alt) return null;
    var m = alt.match(POSITION_RE);
    if (!m) return null;
    var n = parseInt(m[1], 10);
    return isNaN(n) ? null : n;
  }

  function parseColor(alt) {
    if (!alt) return null;
    var parts = alt.split('#');
    if (parts.length < 2) return null;
    var color = parts[parts.length - 1].trim();
    return color === '' ? null : color;
  }

  function indexDataIslands() {
    document.querySelectorAll('script[data-nelow-media-order]').forEach(function (script) {
      var sectionId = script.getAttribute('data-nelow-media-order');
      if (sectionId && sectionIds.indexOf(sectionId) === -1) {
        sectionIds.push(sectionId);
      }
      var entries;
      try {
        entries = JSON.parse(script.textContent);
      } catch (e) {
        return;
      }
      if (!Array.isArray(entries)) return;
      entries.forEach(function (entry) {
        if (!entry || entry.id == null) return;
        var pos = parsePosition(entry.alt);
        if (pos !== null) {
          positionByMediaId[String(entry.id)] = pos;
        }
        var color = parseColor(entry.alt);
        if (color !== null) {
          colorByMediaId[String(entry.id)] = color;
        }
      });
    });
  }

  function mediaIdFromWrapper(el) {
    var attr = el.getAttribute('data-media-id');
    if (!attr) return null;
    var m = attr.match(MEDIA_ID_RE);
    return m ? m[1] : null;
  }

  function colorFromWrapper(el) {
    var mediaId = mediaIdFromWrapper(el);
    if (mediaId === null || !Object.prototype.hasOwnProperty.call(colorByMediaId, mediaId)) return null;
    return colorByMediaId[mediaId];
  }

  // Determines which color is "active" for this specific container right
  // now. Never guesses across containers: each gallery instance (main
  // slider, thumbnail rail, stacked view, zoom modal, mobile duplicates...)
  // is resolved independently from its own current DOM state.
  function getActiveColor(children) {
    // a) Ground truth once Eurus has filtered the gallery for a variant
    // change: any media Eurus flagged as active tells us the real color.
    for (var i = 0; i < children.length; i++) {
      if (children[i].classList.contains('media_active')) {
        var activeColor = colorFromWrapper(children[i]);
        if (activeColor && activeColor !== ALL_COLOR) return activeColor;
      }
    }
    // b) Fresh load, before any variant change: the server always renders
    // the selected variant's featured media first (see
    // snippets/media-gallery.liquid, show_first_image_avaiable block), so
    // the first resolvable, non-"#ALL" color in current DOM order is the
    // initially selected color.
    for (var j = 0; j < children.length; j++) {
      var firstColor = colorFromWrapper(children[j]);
      if (firstColor && firstColor !== ALL_COLOR) return firstColor;
    }
    return null;
  }

  function reorderContainer(container) {
    var children = Array.prototype.filter.call(container.children, function (el) {
      return el.hasAttribute && el.hasAttribute('data-media-id');
    });
    if (children.length < 2) return;

    var activeColor = getActiveColor(children);
    if (!activeColor) return; // can't resolve a color safely — leave untouched

    // Only media belonging to the active color, or shared "#ALL" media,
    // ever get sorted or moved. Every other color's media is left exactly
    // where it is, relative to itself, so colors can never mix.
    var activeSet = [];
    children.forEach(function (el, idx) {
      var color = colorFromWrapper(el);
      if (color === activeColor || color === ALL_COLOR) {
        var mediaId = mediaIdFromWrapper(el);
        var pos = mediaId !== null && Object.prototype.hasOwnProperty.call(positionByMediaId, mediaId)
          ? positionByMediaId[mediaId]
          : null;
        activeSet.push({ el: el, idx: idx, pos: pos === null ? Infinity : pos });
      }
    });
    if (activeSet.length < 2) return;

    var originalOrder = activeSet.map(function (d) { return mediaIdFromWrapper(d.el); });

    var decorated = activeSet.slice().sort(function (a, b) {
      if (a.pos !== b.pos) return a.pos - b.pos;
      return a.idx - b.idx; // stable fallback: keep original relative order
    });

    var newOrder = decorated.map(function (d) { return mediaIdFromWrapper(d.el); });
    var changed = newOrder.some(function (id, i) { return id !== originalOrder[i]; });

    // Even when the sort order itself hasn't changed, the active subset must
    // physically sit at the front of the container (mirrors Eurus's own
    // _setActiveMedia(), which prepends the active set). Only skip the DOM
    // write when both the order AND the front-of-container position are
    // already correct, to stay idempotent and avoid retriggering observers.
    var alreadyAtFront = true;
    for (var k = 0; k < decorated.length; k++) {
      if (children[k] !== decorated[k].el) { alreadyAtFront = false; break; }
    }
    if (!changed && alreadyAtFront) return;

    var frag = document.createDocumentFragment();
    decorated.forEach(function (d) { frag.appendChild(d.el); });
    container.insertBefore(frag, container.firstChild);

    var splideRoot = container.closest('.splide');
    if (splideRoot && splideRoot.splide) {
      try {
        splideRoot.splide.refresh();
        splideRoot.splide.go(0);
      } catch (e) {
        /* noop — never let a Splide refresh error break the page */
      }
    }
  }

  var reordering = false;

  function reorderAll() {
    if (reordering) return;
    reordering = true;
    try {
      var containers = new Set();
      document.querySelectorAll('[data-media-id]').forEach(function (el) {
        if (el.parentElement) containers.add(el.parentElement);
      });
      containers.forEach(reorderContainer);
    } finally {
      // Release on next tick so our own DOM mutations don't retrigger observers synchronously.
      setTimeout(function () { reordering = false; }, 0);
    }
  }

  var observer = new MutationObserver(function () {
    if (reordering) return;
    reorderAll();
  });

  function observeContainers() {
    document.querySelectorAll('[data-media-id]').forEach(function (el) {
      if (el.parentElement) {
        observer.observe(el.parentElement, { childList: true });
      }
    });
  }

  function init() {
    indexDataIslands();
    reorderAll();
    observeContainers();

    // Primary, explicit hook: Eurus dispatches this event after it finishes
    // filtering the gallery for the newly selected variant/color.
    sectionIds.forEach(function (sectionId) {
      document.addEventListener('eurus:product-page-variant-select:updated:' + sectionId, function () {
        reorderAll();
        // Containers can be re-rendered by AJAX (quick view); re-observe just in case.
        observeContainers();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Quick view / AJAX-loaded product sections can inject new data islands and
  // galleries after this script already ran once.
  document.addEventListener('shopify:section:load', function () {
    indexDataIslands();
    reorderAll();
    observeContainers();
  });

  // Splide itself is loaded lazily on the real storefront (assets/theme.js,
  // deferScriptLoad): only on the first user interaction (mouseover / wheel
  // / scroll / keydown) or after a 1-3s timeout, whichever comes first. That
  // is a genuine, non-deterministic race against this script's own
  // DOMContentLoaded pass. `slider loaded` is dispatched by deferScriptLoad
  // right after Splide's script has actually loaded, so listening for it
  // guarantees one final, correctly-ordered resync + splide.refresh() the
  // moment Splide truly mounts, regardless of when that happens.
  document.addEventListener('slider loaded', function () {
    reorderAll();
    observeContainers();
  });
})();
